import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useGetLedgerAccountById, useListPenaltyReviews, useApprovePenaltyReview, useRejectPenaltyReview, useOverridePenaltyReview, useGrantPenaltyGrace, getListPenaltyReviewsQueryKey } from '../../../services/loanService';
import type { PenaltyReview } from '../../../services/schemas/loanServiceSchemas/penaltyReview';
import { PenaltyReviewStatus } from '../../../services/schemas/loanServiceSchemas/penaltyReviewStatus';
import { formatCurrency, formatDate, getPreferredDateInputValue } from '../../../utils/formatters';
import DataTable from 'react-data-table-component';
import { toast } from 'react-toastify';
import { Modal as BootstrapModal } from 'bootstrap';
import { Modal as ReactModal } from 'react-bootstrap';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import './modal.css'; // Import custom modal CSS
import LoanDefaultTermsCard from './LoanDefaultTermsCard';

const LoanAccountPenalties = () => {
  const { loanAccountId } = useParams<{ loanAccountId: string }>();
  const navigate = useNavigate();
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showGraceModal, setShowGraceModal] = useState(false);
  const [graceDays, setGraceDays] = useState('');
  const [graceReason, setGraceReason] = useState('');
  const [selectedPenalty, setSelectedPenalty] = useState<PenaltyReview | null>(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [overrideAmount, setOverrideAmount] = useState<number>(0);
  const [overrideReason, setOverrideReason] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [approvalDueDate, setApprovalDueDate] = useState('');

  const { data: loanAccount, isLoading: isLoadingLoanAccount } = useGetLedgerAccountById(loanAccountId || '');
  const { data: reviewsPage, isLoading: isLoadingPenalties, refetch: refetchPenalties } = useListPenaltyReviews(
    { accountId: loanAccountId || undefined, status: PenaltyReviewStatus.PENDING, page: 0, size: 100 },
    { query: { enabled: !!loanAccountId } }
  );
  const penalties: PenaltyReview[] = reviewsPage?.content || [];
  const queryClient = useQueryClient();

  // Refreshes every pending-review list, so the header alert drops a decided charge straight away
  const refreshPendingCharges = async () => {
    await queryClient.invalidateQueries({ queryKey: [getListPenaltyReviewsQueryKey()[0]] });
    await refetchPenalties();
  };
  const approvePenaltyMutation = useApprovePenaltyReview();
  const rejectPenaltyMutation = useRejectPenaltyReview();
  const grantGraceMutation = useGrantPenaltyGrace();
  const overridePenaltyMutation = useOverridePenaltyReview();

  const handleOpenApproveModal = (penalty: PenaltyReview) => {
    setSelectedPenalty(penalty);
    setApprovalDueDate(getPreferredDateInputValue(
      // newDueDate is not declared on the penalty response schemas; read defensively so the
      // fallback below still applies if the API starts returning it.
      (penalty?.calculated as any)?.newDueDate,
      (penalty as any)?.newDueDate || loanAccount?.firstDueDate || ''
    ));
    setShowApproveModal(true);
  };

  const handleApprove = async () => {
    if (!selectedPenalty || !selectedPenalty.id) {
      toast.error('Invalid penalty review ID');
      return;
    }
    try {
      await approvePenaltyMutation.mutateAsync({
        reviewId: selectedPenalty.id,
        data: {
          ...(approvalDueDate ? { newDueDate: approvalDueDate } : {}),
          remarks: 'Approved by loan officer'
        }
      });
      toast.success('Penalty approved successfully');
      setShowApproveModal(false);
      setApprovalDueDate('');
      setSelectedPenalty(null);
      // Refresh the list after successful approval
      await refreshPendingCharges();
    } catch (error) {
      console.error('Error approving penalty:', error);
      toast.error('Failed to approve penalty');
    }
  };

  const handleReject = async () => {
    if (!selectedPenalty || !selectedPenalty.id) return;

    try {
      await rejectPenaltyMutation.mutateAsync({
        reviewId: selectedPenalty.id,
        data: {
          remarks: rejectionReason
        }
      });
      toast.success('Penalty rejected successfully');
      setShowRejectModal(false);
      setRejectionReason('');
      // Refresh the list after successful rejection
      await refreshPendingCharges();
    } catch (error) {
      console.error('Error rejecting penalty:', error);
      toast.error('Failed to reject penalty');
    }
  };

  const graceDaysValue = Number(graceDays);
  const graceDaysValid = graceDays !== '' && Number.isInteger(graceDaysValue) && graceDaysValue >= 1;

  // Grace on this charge: it comes off the pending list, and if the client is still unpaid once
  // the grace runs out, the nightly run proposes the charge again.
  const handleGrantGrace = async () => {
    if (!selectedPenalty?.id || !graceDaysValid) return;

    try {
      const review = await grantGraceMutation.mutateAsync({
        reviewId: selectedPenalty.id,
        data: { graceDays: graceDaysValue, remarks: graceReason || undefined }
      });
      toast.success(`Grace granted until ${formatDate(review?.graceUntil || '')}`);
      setShowGraceModal(false);
      setGraceDays('');
      setGraceReason('');
      await refreshPendingCharges();
    } catch (error) {
      console.error('Error granting grace:', error);
      toast.error('Failed to grant grace');
    }
  };

  const handleOpenOverrideModal = (penalty: PenaltyReview) => {
    setSelectedPenalty(penalty);
    setOverrideAmount(penalty.calculated?.amountDec || 0);
    setShowOverrideModal(true);

    // Show the modal using Bootstrap's Modal class
    const modalElement = document.getElementById('DueCollectionOverride');
    if (modalElement) {
      const modal = new BootstrapModal(modalElement);
      modal.show();
    }
  };

  const handleOverride = async () => {
    if (!selectedPenalty || !selectedPenalty.id) return;

    try {
      await overridePenaltyMutation.mutateAsync({ 
        reviewId: selectedPenalty.id, 
        data: { overrideAmount, remarks: overrideReason } 
      });
      toast.success('Penalty overridden successfully');
      
      // Close the modal using Bootstrap's Modal class
      const modalElement = document.getElementById('DueCollectionOverride');
      if (modalElement) {
        const modal = BootstrapModal.getInstance(modalElement);
        if (modal) {
          modal.hide();
        }
      }
      
      setShowOverrideModal(false);
      setOverrideAmount(0);
      setOverrideReason('');
      // Refresh the list after successful override
      await refreshPendingCharges();
    } catch (error) {
      console.error('Error overriding penalty:', error);
      toast.error('Failed to override penalty');
    }
  };

  const columns = [
    {
      name: 'As Of Date',
      selector: (row: PenaltyReview) => row.calculated?.asOfDate || '',
      sortable: true,
      format: (row: PenaltyReview) => formatDate(row.calculated?.asOfDate || ''),
    },
    {
      name: 'Penalty Amount',
      selector: (row: PenaltyReview) => row.calculated?.amountDec || 0,
      sortable: true,
      format: (row: PenaltyReview) => formatCurrency(row.calculated?.amountDec || 0),
    },
    {
      name: 'Status',
      selector: (row: PenaltyReview) => row.status || '',
      sortable: true,
      cell: (row: PenaltyReview) => {
        const getStatusColor = (status?: string) => {
          switch (status) {
            case 'PENDING':
              return 'warning';
            case 'APPROVED':
              return 'success';
            case 'REJECTED':
              return 'danger';
            case 'OVERRIDDEN':
              return 'info';
            default:
              return 'secondary';
          }
        };

        return (
          <span className={`badge bg-${getStatusColor(row.status)}`}>
            {row.status}
          </span>
        );
      },
    },
    {
      name: 'Actions',
      cell: (row: PenaltyReview) => {
        // Only show actions if status is PENDING
        if (row.status !== 'PENDING') {
          return (
            <span className="text-muted small">
              {row.status === 'APPROVED' ? 'Approved' : 
               row.status === 'REJECTED' ? 'Rejected' : 
               row.status === 'OVERRIDDEN' ? 'Overridden' : row.status}
            </span>
          );
        }

        return (
          <div className="d-flex gap-1">
            <button
              className="btn btn-icon btn-sm btn-success"
              onClick={() => handleOpenApproveModal(row)}
              disabled={approvePenaltyMutation.isPending}
              title="Approve"
            >
              <i className="fa fa-check"></i>
            </button>
            <button
              className="btn btn-icon btn-sm btn-danger"
              onClick={() => {
                setSelectedPenalty(row);
                setShowRejectModal(true);
              }}
              disabled={rejectPenaltyMutation.isPending}
              title="Reject"
            >
              <i className="fa fa-times"></i>
            </button>
            <button
              className="btn btn-icon btn-sm btn-warning"
              onClick={() => {
                setSelectedPenalty(row);
                setShowGraceModal(true);
              }}
              disabled={grantGraceMutation.isPending}
              title="Grant grace"
            >
              <i className="fa fa-hourglass-half"></i>
            </button>
            <button
              className="btn btn-icon btn-sm btn-info"
              onClick={() => handleOpenOverrideModal(row)}
              disabled={overridePenaltyMutation.isPending}
              title="Override"
            >
              <i className="fa fa-edit"></i>
            </button>
          </div>
        );
      },
    },
  ];

  // Add useEffect to handle modal cleanup
  useEffect(() => {
    const overrideModal = document.getElementById('DueCollectionOverride');
    if (overrideModal) {
      const modal = new BootstrapModal(overrideModal);
      overrideModal.addEventListener('hidden.bs.modal', () => {
        setShowOverrideModal(false);
        setSelectedPenalty(null);
        setOverrideAmount(0);
        setOverrideReason('');
      });
    }
  }, []);

  if (!loanAccountId) {
    return (
      <div className="text-center py-5">
        <p>Invalid loan account ID</p>
        <button className="btn btn-primary mt-3" onClick={() => navigate('/collections/penalties')}>
          Back to Penalties
        </button>
      </div>
    );
  }

  if (isLoadingLoanAccount || isLoadingPenalties) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2">Loading loan account details...</p>
      </div>
    );
  }

  return (
    <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1 mt-0 mt-lg-3">
      <div className="container-fluid">
        {/* Loan Account Summary */}
        {loanAccount && (
          <div className="row g-3 mb-4">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h5 className="card-title mb-0">Loan Account Summary</h5>
                </div>
                <div className="card-body">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <div className="d-flex align-items-center">
                        <div className="avatar-sm me-3">
                          <div className="avatar-title bg-soft-primary text-primary rounded-circle">
                            <i className="fa fa-user"></i>
                          </div>
                        </div>
                        <div>
                          <h6 className="text-muted mb-1 small">Customer Number</h6>
                          <div className="h6 fw-bold mb-0">{loanAccount.customerNumber}</div>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="d-flex align-items-center">
                        <div className="avatar-sm me-3">
                          <div className="avatar-title bg-soft-info text-info rounded-circle">
                            <i className="fa fa-file-text"></i>
                          </div>
                        </div>
                        <div>
                          <h6 className="text-muted mb-1 small">Account Number</h6>
                          <div className="h6 fw-bold mb-0">{loanAccount.accountNumber}</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="row g-3 mt-3">
                    <div className="col-md-4">
                      <div className="p-3 bg-light rounded">
                        <h6 className="text-muted mb-1 small">Disbursed Amount</h6>
                        <div className="h5 fw-bold mb-0">{formatCurrency(loanAccount.disbursedAmount || 0)}</div>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="p-3 bg-light rounded">
                        <h6 className="text-muted mb-1 small">Total Outstanding</h6>
                        <div className="h5 fw-bold mb-0">{formatCurrency(loanAccount.totalOutstanding || 0)}</div>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="p-3 bg-light rounded">
                        <h6 className="text-muted mb-1 small">Status</h6>
                        <div className="h6 fw-bold mb-0">{loanAccount.status || 'N/A'}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Pending Penalties */}
        <div className="row g-3 mb-4">
          <div className="col-12">
            {loanAccountId && <LoanDefaultTermsCard accountId={loanAccountId} onSaved={refreshPendingCharges} />}
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="card-title mb-0">Pending Charges</h5>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => navigate('/collections/penalties')}
                >
                  <i className="fa fa-arrow-left me-1"></i>
                  Back to All Charges
                </button>
              </div>
              <div className="card-body">
                {penalties && penalties.length > 0 ? (
                  <DataTable
                    columns={columns}
                    data={penalties}
                    pagination
                    highlightOnHover
                    striped
                    responsive
                  />
                ) : (
                  <div className="text-center py-5">
                    <p className="text-muted">No pending charges found</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Approve Modal */}
      <ReactModal show={showApproveModal} onHide={() => {
        setShowApproveModal(false);
        setSelectedPenalty(null);
        setApprovalDueDate('');
      }}>
        <ReactModal.Header closeButton>
          <ReactModal.Title>Approve Penalty</ReactModal.Title>
        </ReactModal.Header>
        <ReactModal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Due Date</Form.Label>
              <Form.Control
                type="date"
                value={approvalDueDate}
                onChange={(e) => setApprovalDueDate(e.target.value)}
              />
              <Form.Text className="text-muted">
                This uses the pending charge’s due date by default. You can keep it or change it before approving.
              </Form.Text>
            </Form.Group>
          </Form>
        </ReactModal.Body>
        <ReactModal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowApproveModal(false);
            setSelectedPenalty(null);
            setApprovalDueDate('');
          }}>
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={handleApprove}
            disabled={!selectedPenalty?.id || approvePenaltyMutation.isPending}
          >
            {approvePenaltyMutation.isPending ? (
              <>
                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                Approving...
              </>
            ) : (
              'Approve'
            )}
          </Button>
        </ReactModal.Footer>
      </ReactModal>

      {/* Override Modal */}
      <div className="modal fade modal-slide-in-left" id="DueCollectionOverride" tabIndex={-1} aria-labelledby="DueCollectionOverrideLabel" aria-hidden="true">
        <div className="modal-dialog modal-dialog-vertical" style={{ maxWidth: '900px', width: '90%', height: '100vh', margin: 0 }}>
          <div className="modal-content rounded-0 d-flex flex-column" style={{ height: '100%' }}>
            <div className="modal-header bg-light border-bottom">
              <h5 className="modal-title d-flex align-items-center" id="DueCollectionOverrideLabel">
                <i className="fa fa-edit me-2 text-info"></i>
                Override Penalty Amount
              </h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div className="modal-body p-4 overflow-auto flex-grow-1">
              <Form>
                <div className="form-section">
                  <h6 className="form-section-title">Penalty Summary</h6>
                  <p className="text-muted mb-4">
                    Review the penalty details before overriding. All fields marked with * are required.
                  </p>

                  {/* Summary Cards */}
                  <div className="row g-3 mb-4">
                    <div className="col-md-6">
                      <div className="card h-100 border-0 shadow-sm">
                        <div className="card-body">
                          <div className="d-flex align-items-center">
                            <div className="avatar-sm me-3">
                              <div className="avatar-title bg-soft-primary text-primary rounded-circle">
                                <i className="fa fa-calendar"></i>
                              </div>
                            </div>
                            <div>
                              <h6 className="text-muted mb-1 small">Payment Due Date</h6>
                          <div className="h6 fw-bold mb-0">{selectedPenalty ? formatDate(selectedPenalty.calculated?.asOfDate || '') : '-'}</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="card h-100 border-0 shadow-sm">
                        <div className="card-body">
                          <div className="d-flex align-items-center">
                            <div className="avatar-sm me-3">
                              <div className="avatar-title bg-soft-warning text-warning rounded-circle">
                                <i className="fa fa-coins"></i>
                              </div>
                            </div>
                            <div>
                              <h6 className="text-muted mb-1 small">Current Penalty Amount</h6>
                              <div className="h6 fw-bold mb-0">{formatCurrency(selectedPenalty?.calculated?.amountDec || 0)}</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Override Details Section */}
                  <div className="form-section">
                    <h6 className="form-section-title">Override Details</h6>
                    <div className="row g-3">
                      <div className="col-md-6">
                        <div className="form-group">
                          <label className="form-label">
                            New Amount <span className="text-danger">*</span>
                          </label>
                          <div className="input-group">
                            <span className="input-group-text bg-light">
                              <i className="fa fa-rand text-muted"></i>
                            </span>
                            <input
                              type="number"
                              className="form-control"
                              value={overrideAmount}
                              onChange={(e) => setOverrideAmount(Number(e.target.value))}
                              min="0"
                              step="0.01"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Reason Section */}
                  <div className="form-section">
                    <h6 className="form-section-title">Override Reason</h6>
                    <div className="row g-3">
                      <div className="col-12">
                        <div className="form-group">
                          <label className="form-label">
                            Reason for Override <span className="text-danger">*</span>
                          </label>
                          <div className="input-group">
                            <span className="input-group-text bg-light">
                              <i className="fa fa-comment text-muted"></i>
                            </span>
                            <textarea
                              className="form-control"
                              rows={3}
                              value={overrideReason}
                              onChange={(e) => setOverrideReason(e.target.value)}
                              placeholder="Enter reason for overriding the penalty amount..."
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Form>
            </div>
            <div className="modal-footer border-top mt-4 pt-3 position-sticky bottom-0 bg-white">
              <button type="button" className="btn btn-light" data-bs-dismiss="modal">
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-info"
                onClick={handleOverride}
                disabled={!overrideAmount || !overrideReason || overridePenaltyMutation.isPending}
              >
                {overridePenaltyMutation.isPending ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                    Overriding...
                  </>
                ) : (
                  'Override'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Reject Modal */}
      <ReactModal show={showRejectModal} onHide={() => setShowRejectModal(false)}>
        <ReactModal.Header closeButton>
          <ReactModal.Title>Reject Penalty</ReactModal.Title>
        </ReactModal.Header>
        <ReactModal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Penalty Amount</Form.Label>
              <Form.Control
                type="text"
                value={formatCurrency(selectedPenalty?.calculated?.amountDec || 0)}
                disabled
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Reason for Rejection</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Enter reason for rejecting the penalty..."
              />
            </Form.Group>
          </Form>
        </ReactModal.Body>
        <ReactModal.Footer>
          <Button variant="secondary" onClick={() => setShowRejectModal(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleReject}
            disabled={!rejectionReason || rejectPenaltyMutation.isPending}
          >
            {rejectPenaltyMutation.isPending ? (
              <>
                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                Rejecting...
              </>
            ) : (
              'Reject'
            )}
          </Button>
        </ReactModal.Footer>
      </ReactModal>

      {/* Grant Grace Modal */}
      <ReactModal show={showGraceModal} onHide={() => setShowGraceModal(false)}>
        <ReactModal.Header closeButton>
          <ReactModal.Title>Grant Grace</ReactModal.Title>
        </ReactModal.Header>
        <ReactModal.Body>
          <p className="text-muted small">
            The charge comes off the pending list and no new charge is proposed during the grace period.
            If the client is still unpaid when it ends, the charge is proposed again.
          </p>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Charge Amount</Form.Label>
              <Form.Control type="text" value={formatCurrency(selectedPenalty?.calculated?.amountDec || 0)} disabled />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Grace (days from today)</Form.Label>
              <Form.Control
                type="number"
                min={1}
                step={1}
                value={graceDays}
                onChange={(e) => setGraceDays(e.target.value)}
                placeholder="e.g. 7"
                isInvalid={graceDays !== '' && !graceDaysValid}
              />
              <Form.Control.Feedback type="invalid">Enter a whole number of days, at least 1</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Reason (optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={graceReason}
                onChange={(e) => setGraceReason(e.target.value)}
                placeholder="e.g. Client committed to pay by Friday"
              />
            </Form.Group>
          </Form>
        </ReactModal.Body>
        <ReactModal.Footer>
          <Button variant="secondary" onClick={() => setShowGraceModal(false)}>
            Cancel
          </Button>
          <Button variant="warning" onClick={handleGrantGrace} disabled={!graceDaysValid || grantGraceMutation.isPending}>
            {grantGraceMutation.isPending ? 'Granting...' : 'Grant grace'}
          </Button>
        </ReactModal.Footer>
      </ReactModal>
    </div>
  );
};

export default LoanAccountPenalties; 